'use client';

import {
  IAuthorResponse, IPagination, IPostInfo, IPostsListResponse,
} from '@sapientpro/sapientpro-data-models';
import classnames from 'classnames';
import { useAtomValue } from 'jotai';
import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import client from '../../../apiClient';
import Img from '../../../components/Img';
import { theme, Theme } from '../../../store/theme';
import AuthorCard from '../AuthorCard';
import styles from './Author.module.scss';
import Breadcrumbs from '../../../components/Breadcrumbs/Breadcrumbs';
import SocialLink from '../../../components/SocialLink';
import FollowBlock from '../../../components/FollowBlock';
import Pagination from '../../../components/Pagination/Pagination';
import ArticleCard from '../ArticlesList/ArticleCard';

type AuthorProps = {
  data: IAuthorResponse,
  limit: number,
};

type GetAuthorPostsParams = {
  slug: string;
  limit: number;
  offset: number;
};

const getAuthorPosts = async ({ slug, limit, offset }: GetAuthorPostsParams) => {
  const defaultParams = { us: 'false', authorSlug: slug };
  try {
    return await client.get<IPostsListResponse>(
      `posts?${new URLSearchParams(JSON.parse(JSON.stringify({ ...defaultParams, limit, offset })))}`,
    );
  } catch (e) {
    console.error(e.message);
  }
};

const Author = ({ data, limit }: AuthorProps) => {
  const params = useSearchParams();
  const appTheme = useAtomValue<Theme>(theme);
  const [renderedPosts, setRenderedPosts] = useState<IPostInfo[]>([]);
  const offsetRef = useRef<number>(0);
  const listRef = useRef<HTMLDivElement>(null);
  const [paginationData, setPaginationData] = useState<IPagination>({
    limit,
    total: 0,
    quantity: 0,
    offset: 0,
  });
  const initPage = Number(params.get('page') || 1) - 1;
  const breadcrumbs = [
    {
      title: 'Home',
      url: '/',
    },
    {
      title: 'Blog',
      url: '/blog',
    }, {
      title: data.author.pseudonym,
      url: `/blog/author/${data.author.slug}`,
    }];

  const loadPosts = async (newOffset?: number) => {
    try {
      const hasOffset = typeof newOffset !== 'undefined';
      const offset = hasOffset ? newOffset : limit * initPage;

      const postsData = await getAuthorPosts({ slug: data.author.slug, limit, offset });
      setPaginationData({ ...paginationData, ...(postsData as IPostsListResponse)?.pagination, offset });
      offsetRef.current = (postsData as IPostsListResponse)?.pagination?.offset || 0;
      setRenderedPosts((postsData as IPostsListResponse)?.posts);
    } catch (e) {
      console.error(e.message);
    }
  };

  const handleOffsetChange = (offset: number) => {
    loadPosts(offset);

    const page = offset / limit + 1;
    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    window.history.pushState(null, '', url.toString());

    const listRect = listRef.current?.getBoundingClientRect();

    window.scrollTo({ top: listRect?.top, behavior: 'smooth' });
  };

  useEffect(() => {
    const handlePopState = () => {
      const searchQueries = window.location.search.split('?')[1]?.split('&') || [];
      const pageQuery = searchQueries.find(query => query.startsWith('page=')) || '';
      const page = Number(pageQuery?.split('page=')?.[1] || 1) - 1;
      const newOffset = page * limit;

      if (newOffset !== offsetRef.current) loadPosts(newOffset);
    };

    window.addEventListener('popstate', handlePopState);

    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    loadPosts();
  }, [data?.author?.slug]);

  return (
    <main className={styles.main}>
      <section className={styles.hero}>
        <div className={styles.heroContentWrapper}>
          <div className={classnames(styles.heroContent, 'container')}>
            <Breadcrumbs
              items={breadcrumbs}
              className={styles.authorBreadcrumbs}
            />
            <div className={styles.author}>
              <div className={styles.author__mainInfo}>
                <div className={styles.avatar}>
                  <Img
                    src={data.author?.avatar?.url || '/'}
                    alt={data.author?.avatar?.alternativeText || 'avatar'}
                    withPreloader
                  />
                </div>
                <div className={styles.info}>
                  <h1 className={classnames(styles.name, 'header2')}>{data.author?.pseudonym}</h1>
                  <p className={classnames(styles.position, 'bodyLarge')}>{data.author.position}</p>
                </div>
              </div>
              {!!data?.author?.socials && (
              <div className={styles.author__socials}>
                {data.author.socials.map((social) => (
                  <SocialLink
                    key={social.id}
                    link={social.link}
                    customIcon={social.icon[appTheme]}
                    className={styles.socialLink}
                  />
                ))}
              </div>
              )}
            </div>
          </div>
        </div>
      </section>
      <section className={classnames(styles.authorDescription, 'container')}>
        <h3 className={classnames(styles.descriptionTitle, 'tabsDark')}>{`About ${data.author.pseudonym}`}</h3>
        <p className={classnames(styles.descriptionText, 'bodyLarge')}>{data.author.description}</p>
      </section>
      <section className={classnames(styles.authorContent, 'container')}>
        <section className={styles.articles}>
          <h2 className={classnames(styles.articlesTitle, 'header3')}>Recent Posts by the Author</h2>
          <div
            className={styles.articles__content}
            ref={listRef}
          >
            {renderedPosts.map(article => (
              <ArticleCard
                className={styles.articleCard}
                article={article}
                key={article.id}
                tableView
              />
            ))}
          </div>
          <Pagination
            data={paginationData}
            handlePageChange={handleOffsetChange}
          />
        </section>
        <aside className={styles.sidebar}>
          <section className={styles.otherAuthors}>
            <h3 className={classnames(styles.trendingTitle, 'header4')}>Explore More Authors</h3>
            <div className={styles.otherAuthorsContent}>
              {data.othersAuthors?.map(otherAuthor => (
                <Link
                  href={otherAuthor.slug}
                  className={styles.authorCard}
                  key={otherAuthor.id}
                >
                  <AuthorCard
                    avatar={otherAuthor.avatar?.url || '/'}
                    name={otherAuthor.pseudonym}
                    info={otherAuthor.position}
                    className={styles.otherAuthorCard}
                  />
                  <div className={styles.authorCard__linkIcon}>
                    <svg>
                      <use
                        width={20}
                        height={20}
                        xlinkHref='/media/icon-nest-author.svg#iconNestAuthor'
                        href='/media/icon-nest-author.svg#iconNestAuthor'
                      />
                    </svg>
                  </div>
                </Link>
              ))}
            </div>
          </section>
          <FollowBlock />
        </aside>
      </section>
    </main>
  );
};

export default Author;
